import React, { useEffect, useMemo, useState } from "react";

import { RadioGroup, Divider, MenuItem } from "@mui/material";
import { ColumnFilter, SearchSuggest } from "Components/AppGrid/shipeezi-new-grid";
import { AppBadge } from "Components/AppBadge";
import { Button, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import { SELECTED_COMPANIES_IDS, SELECTED_TASK_TYPES } from "Constants";
import { useAppState } from "Context/StateContext";
import isEqual from "fast-deep-equal";
import { ArrowUp2 as IconArrowUp, ArrowDown2 as IconArrowDown } from "iconsax-react";
import { Searches } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";

import useEquipmentActions from "../../../../../../pages/private/Shared/Settings/Resources/FleetPage/hooks/useEquipmentActions";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import {
  PopoverButton,
  Popover,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Header,
  HeaderTitle,
  HeaderSubtitle,
  ActionSection,
  AccordionContent,
  FormControlLabel,
  Content,
  Checkbox,
  Radio,
  MenuItemBranch,
  FormBranchLabel,
} from "./styled";
import { DespatchGlobalFilters, getDefaultDespatchFilterValues } from "./types";

export const DespatchFilters = () => {
  const { currentUser } = useAppState();

  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState<HTMLElement | null>(null);
  const {
    setDespatchGlobalFilters,
    despatchGlobalFilters,
    setDespatchSearchColumns,
    despatchSearchColumns,
  } = useOperationDespatchContext();

  const { getAllWarehouses, getAllTaskTypes } = useEquipmentActions();
  const { getCompaniesWithConnection } = useDespatchActions();

  const { t: translate } = useTranslation();

  const { data: taskTypes } = getAllTaskTypes();
  const { data: warehouses } = getAllWarehouses();
  const { data: companies } = getCompaniesWithConnection();

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const savedSelectedIds = localStorage.getItem(SELECTED_COMPANIES_IDS);
  const savedTaskTypes = localStorage.getItem(SELECTED_TASK_TYPES);

  const [filters, setFilters] = useState<DespatchGlobalFilters>(
    getDefaultDespatchFilterValues(
      currentUser?.companyId!,
      savedSelectedIds ? JSON.parse(savedSelectedIds) : [currentUser?.companyId!],
      savedTaskTypes ? JSON.parse(savedTaskTypes) : []
    )
  );

  const areFiltersEqual = useMemo(() => {
    if (!optionsMenuAnchor) return false;

    return isEqual(filters, despatchGlobalFilters) && isEqual(despatchSearchColumns, searchOptions);
  }, [optionsMenuAnchor, filters, despatchGlobalFilters, despatchSearchColumns, searchOptions]);

  const filtersLength = useMemo(() => {
    const sizeOfCompaniesSelected = despatchGlobalFilters.companyIds.length;
    const sizeOfSearches = despatchSearchColumns?.length ?? 0;

    return despatchGlobalFilters.tasksTypeNames.length + sizeOfSearches + sizeOfCompaniesSelected;
  }, [currentUser, despatchGlobalFilters, despatchSearchColumns]);

  const searchColumns = useMemo(() => {
    return [
      { name: "leader", title: translate("leaderName") },
      { name: "members", title: translate("memberName") },
      { name: "orderReference", title: translate("orderReference") },
      { name: "equipment", title: translate("equipmentName") },
      { name: "storageBoxes", title: translate("storageBoxNumber") },
    ];
  }, []);

  useEffect(() => {
    if (optionsMenuAnchor) {
      setFilters({ ...despatchGlobalFilters });
      setSearchOptions(despatchSearchColumns);
      setSearchInput("");
    }
  }, [optionsMenuAnchor]);

  const resetLocalStorage = () => {
    localStorage.setItem(SELECTED_COMPANIES_IDS, JSON.stringify([currentUser?.companyId!]));
    localStorage.removeItem(SELECTED_TASK_TYPES);
  };

  const resetFilter = () => {
    setSearchOptions([]);
    setSearchInput("");
    setFilters(
      getDefaultDespatchFilterValues(currentUser?.companyId!, [currentUser?.companyId!], [])
    );

    resetLocalStorage();
  };

  const handleChangeDepartament = (event: React.ChangeEvent<HTMLInputElement>) => {
    const departament = (event.target as HTMLInputElement).value as "warehouse" | "road";

    setFilters({
      ...filters,
      departament,
    });
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const updateTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;

    setFilters({
      ...filters,
      searchTerm: text,
    });
  };

  const applyFilters = () => {
    setDespatchGlobalFilters({ ...filters });
    setDespatchSearchColumns(searchOptions);
    setOptionsMenuAnchor(null);
  };

  const updateFilterArrays = (
    name: "companyIds" | "branchIds" | "tasksTypeNames" | "warehouseIds",
    active: boolean,
    value: number | string
  ) => {
    const fieldValue = filters[name] as any[];
    const newFieldValue = active
      ? [...fieldValue, value]
      : fieldValue.filter((currentValue: any) => currentValue !== value);

    setFilters({
      ...filters,
      [name]: newFieldValue,
    });
  };

  const storeSelectedCompaniesIds = (active: boolean, companyId: number) => {
    const currentCompaniesFiltered = filters.companyIds;

    const companiesIdsToStore = active
      ? [...currentCompaniesFiltered, companyId]
      : currentCompaniesFiltered.filter((currentId: any) => currentId !== companyId);

    localStorage.setItem(SELECTED_COMPANIES_IDS, JSON.stringify(companiesIdsToStore));
  };

  const storeSelectedTaskTypes = (active: boolean, taskType: string) => {
    const currentTaskTypesFiltered: string[] =
      JSON.parse(localStorage.getItem(SELECTED_TASK_TYPES)!) || [];

    const taskTypesToStore = active
      ? [...currentTaskTypesFiltered, taskType]
      : currentTaskTypesFiltered.filter((currentTaskType: any) => currentTaskType !== taskType);

    localStorage.setItem(SELECTED_TASK_TYPES, JSON.stringify(taskTypesToStore));
  };

  const onSelectSearch = (options: ColumnFilter[]) => {
    const newSearches: Searches = [];

    options.forEach((option) => {
      if (option.value) newSearches.push({ [option.name]: option.value! });
    });
    setSearchOptions(options);
  };

  const renderHeaderSection = () => {
    return (
      <Header>
        <HeaderTitle>{translate("dispatchFilters")}</HeaderTitle>

        <HeaderSubtitle>{translate("theFilteredDataAffectsWhatYouSeeOnDispatch")}</HeaderSubtitle>

        <SearchSuggest
          columns={searchColumns}
          columnsSelected={searchOptions}
          onChangeColumnsSelected={onSelectSearch}
          inputValue={searchInput}
          onChangeInput={setSearchInput}
          placeholder={`${translate("search")}...`}
        />
      </Header>
    );
  };

  const renderCompaniesSection = () => {
    return (
      <Accordion elevation={0}>
        <AccordionSummary expandIcon={<IconArrowUp size={20} />}>
          {translate("companies")}
        </AccordionSummary>

        <AccordionDetails>
          <AccordionContent>
            {companies.map((company) => {
              const isChecked = filters.companyIds.includes(company.id!);
              const disabled = isChecked && filters.companyIds?.length === 1;

              return (
                <FormControlLabel
                  key={company.id!}
                  control={
                    <Checkbox
                      checked={isChecked}
                      disabled={disabled}
                      onChange={() => {
                        updateFilterArrays("companyIds", !isChecked, company.id!);
                        storeSelectedCompaniesIds(!isChecked, company.id!);
                      }}
                    />
                  }
                  label={company.name}
                />
              );
            })}
          </AccordionContent>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderWarehouses = () => {
    if (filters.departament !== ("warehouse" as const)) return;

    return (
      <Select placeholder={translate("selectAWarehouse")}>
        {warehouses.map((item) => {
          return (
            <MenuItem key={item.id!} value={item.id!}>
              {item.name}
            </MenuItem>
          );
        })}
      </Select>
    );
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const renderDepartamentsSection = () => {
    return (
      <Accordion elevation={0}>
        <AccordionSummary expandIcon={<IconArrowUp size={20} />}>Departaments</AccordionSummary>

        <AccordionDetails>
          <RadioGroup
            aria-labelledby="departament-radio-buttons-group-label"
            onChange={handleChangeDepartament}
            value={filters.departament}
          >
            <FormControlLabel
              value="warehouse"
              control={<Radio />}
              label={translate("warehouse")}
            />

            {renderWarehouses()}

            <FormControlLabel value="road" control={<Radio />} label={translate("road")} />
          </RadioGroup>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBranchesSection = () => {
    return (
      <Accordion elevation={0}>
        <AccordionSummary expandIcon={<IconArrowUp size={20} />}>
          {translate("branch")}
        </AccordionSummary>

        <AccordionDetails>
          <AccordionContent>
            {!!currentUser?.branches?.length &&
              currentUser?.branches.map((branch) => {
                const isChecked = filters.branchIds.includes(branch.id!);

                return (
                  <MenuItemBranch>
                    <FormControlLabel
                      key={branch.id!}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => {
                            updateFilterArrays("branchIds", !isChecked, branch.id!);
                          }}
                        />
                      }
                      label={branch.name}
                    />
                    {!!currentUser.totalCompaniesWithNetwork && (
                      <FormBranchLabel>{branch.company?.name}</FormBranchLabel>
                    )}
                  </MenuItemBranch>
                );
              })}
          </AccordionContent>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderTasksSection = () => {
    return (
      <Accordion elevation={0}>
        <AccordionSummary expandIcon={<IconArrowUp size={20} />}>
          {translate("taskType")}
        </AccordionSummary>

        <AccordionDetails>
          <AccordionContent>
            {taskTypes.map((taskType) => {
              const isChecked = filters.tasksTypeNames.includes(taskType.name!);

              return (
                <FormControlLabel
                  key={taskType.id!}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={() => {
                        updateFilterArrays("tasksTypeNames", !isChecked, taskType.name!);
                        storeSelectedTaskTypes(!isChecked, taskType.name!);
                      }}
                    />
                  }
                  label={taskType.name}
                />
              );
            })}
          </AccordionContent>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderActionSection = () => {
    return (
      <ActionSection>
        <Button customColor={Color.GRAY} size="small" onClick={resetFilter}>
          {translate("reset")}
        </Button>

        <Button
          customColor={Color.GRAY}
          size="small"
          onClick={applyFilters}
          disabled={areFiltersEqual}
        >
          {translate("apply")}
        </Button>
      </ActionSection>
    );
  };

  return (
    <div>
      <PopoverButton
        onClick={(event) => setOptionsMenuAnchor(event.currentTarget)}
        variant={filtersLength ? "contained" : "outlined"}
        disableElevation
        color={filtersLength ? "primary" : "inherit"}
        size="small"
        endIcon={<IconArrowDown size={16} />}
        isActive={!!filtersLength}
      >
        {translate("dispatchFilters")}

        <AppBadge style={{ marginLeft: "10px" }} variant="primary-inverse" hideOnFalse>
          {filtersLength}
        </AppBadge>
      </PopoverButton>

      <Popover
        anchorEl={optionsMenuAnchor}
        onClose={() => setOptionsMenuAnchor(null)}
        open={Boolean(optionsMenuAnchor)}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        {renderHeaderSection()}

        <Divider />

        <Content>
          {renderCompaniesSection()}

          {/* {renderDepartamentsSection()} */}

          {renderBranchesSection()}

          {renderTasksSection()}
        </Content>

        <Divider />

        {renderActionSection()}
      </Popover>
    </div>
  );
};
